import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Users, Award, Target, Calendar, Database, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { NativeSelect } from './ui/native-select';
import api, { dashboardAPI, objetivosAPI, vendasAPI } from '../services/api';
import { ModuleInfo } from './ui/module-info';
import { DataOriginBadge, DataOriginLegend } from './ui/data-origin';
// DetalheTurnosDia removido do dashboard (limpeza UX)

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MESES_KEYS = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const COLORS = ['hsl(186, 90%, 46%)', 'hsl(158, 76%, 52%)', 'hsl(210, 100%, 27%)', 'hsl(174, 79%, 44%)', 'hsl(197, 63%, 72%)'];

// Mapeamento de secções (constante global do componente)
const MAPEAMENTO_SECOES = {
  'cafe': 'Café',
  'loja_avelal': 'Loja Avelal',
  'loja_satao': 'Loja Sátão',
  'escritorio': 'Escritório',
  'raspadinhas': 'Raspadinhas',
  'restaurante': 'Restaurante',
  'jogos': 'Jogos'
};

const AdminDashboardTab = () => {
  const [rankingMensal, setRankingMensal] = useState([]);
  const [rankingAnual, setRankingAnual] = useState([]);
  const [objetivos, setObjetivos] = useState([]);
  const [objetivosAgrupados, setObjetivosAgrupados] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [vendasAnuais, setVendasAnuais] = useState([]);
  const [stats, setStats] = useState(null);
  const [monitorizacaoRes, setMonitorizacaoRes] = useState({});
  const [vendasPorSeccaoMonitorizacao, setVendasPorSeccaoMonitorizacao] = useState({});
  const [loading, setLoading] = useState(true);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  
  // Estados para Contribuições XD (Source of Truth)
  const [contribuicoesXdMes, setContribuicoesXdMes] = useState(null);
  const [contribuicoesXdAno, setContribuicoesXdAno] = useState(null);
  // 🆕 06-06-2026 — Alerta colaboradores XD com vendas SEM mapping validado
  // (ex: Liliana Natali a trabalhar mas ausente do ranking).
  const [pendentesMapping, setPendentesMapping] = useState(null);
  
  // Estado para expandir/recolher equipa nos cards de objetivos.
  // Se a chave NÃO existe → considera-se EXPANDIDA (default open).
  // Se for `false` → user fechou explicitamente.
  const [expandedTeams, setExpandedTeams] = useState({});

  const toggleTeam = (key) => {
    // default open: undefined → false (fechar) | false → true (abrir) | true → false (fechar)
    setExpandedTeams(prev => ({
      ...prev,
      [key]: prev[key] === undefined ? false : !prev[key],
    }));
  };

  const isTeamExpanded = (key) =>
    expandedTeams[key] === undefined || expandedTeams[key] === true;

  // Tag exibida ao lado de "Equipa": indica origem dos dados da secção
  const seccaoBadge = (seccao, fonte) => {
    const sLow = (seccao || '').toLowerCase();
    if (sLow === 'raspadinhas' || sLow === 'jogos') {
      return { label: seccao, classes: 'bg-purple-50 text-purple-700 border-purple-200' };
    }
    if (fonte === 'xd') {
      return { label: 'XD', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    return null;
  };

  // Estados para Pontos XD (Atingimento Mensal)
  const [pontosXdMes, setPontosXdMes] = useState(null);
  const [pontosXdAno, setPontosXdAno] = useState(null);
  const [recalculandoXd, setRecalculandoXd] = useState(false);
  // 🚀 04-06-2026 — Estado para distinguir "Fase 2 ainda a carregar" de "carregou vazio".
  // Sem isto, o user vê "Sem dados de pontos" durante os 5-45s em que a Fase 2 corre.
  const [loadingXd, setLoadingXd] = useState(true);

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');


  // Função para buscar breakdown por colaborador (agora usa XD como fonte primária)
  const getBreakdownSeccao = (seccao, isAnual = false) => {
    // Tentar dados XD primeiro (contribuições por CreationUserId)
    const contribXd = isAnual ? contribuicoesXdAno : contribuicoesXdMes;
    if (contribXd?.colaboradores) {
      const colaboradores = [];
      const totalSeccao = contribXd.colaboradores.reduce((sum, c) => {
        const sec = c.por_seccao?.[seccao];
        return sum + (sec?.total || 0);
      }, 0);

      for (const colab of contribXd.colaboradores) {
        const sec = colab.por_seccao?.[seccao];
        if (sec && sec.total > 0) {
          colaboradores.push({
            colaborador_nome: colab.nome_xd,
            total: sec.total,
            percentagem: totalSeccao > 0 ? (sec.total / totalSeccao) * 100 : 0,
            fonte: 'xd',
          });
        }
      }

      if (colaboradores.length > 0) {
        return { colaboradores, fonte: 'xd' };
      }
    }

    // Fallback
    return null;
  };

  useEffect(() => {
    loadData();
  }, [mesSelecionado, anoSelecionado]);
  
  // Recarregar dados quando uma venda é modificada
  useEffect(() => {
    const handleVendaModificada = () => {
      loadData();
    };
    
    window.addEventListener('vendaModificada', handleVendaModificada);
    
    return () => {
      window.removeEventListener('vendaModificada', handleVendaModificada);
    };
  }, [mesSelecionado, anoSelecionado]);

  const loadData = async () => {
    try {
      // 🚀 04-06-2026 — Render progressivo (sessão 11):
      // Fase 1 (RÁPIDA, com timeout 10s): só dados Mongo locais que NUNCA falham
      //   → Mostra UI assim que chegam (ou após 10s, o que ocorrer primeiro).
      // Fase 2 (lenta, em background): TUDO o que toca XD (Tailscale + cache cold start).

      // Helper: aplica timeout a uma promise. Se exceder, rejeita silenciosamente
      // — o Promise.allSettled apanha como rejected e usa o default.
      // 🛡️ 06-06-2026: TTL 10s era pouco para cold-start em produção K8s.
      // Aumentado para 25s — suficiente para ranking/mensal cold (9-10s) sem
      // bloquear a UI indefinidamente.
      const withTimeout = (promise, ms = 25000) => Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error(`timeout ${ms}ms`)), ms)),
      ]);

      const fastResults = await Promise.allSettled([
        withTimeout(dashboardAPI.getRankingMensal({ mes: mesSelecionado, ano: anoSelecionado })),  // 0
        withTimeout(objetivosAPI.get(`/objetivos-vendas/${anoSelecionado}`)),                        // 1
        withTimeout(objetivosAPI.get(`/objetivos-vendas-mensal/${anoSelecionado}/${mesSelecionado}`)),// 2
        withTimeout(vendasAPI.getAll({ mes: mesSelecionado, ano: anoSelecionado })),                 // 3
        withTimeout(vendasAPI.getAll({ ano: anoSelecionado })),                                       // 4
        withTimeout(dashboardAPI.getStats({ mes: mesSelecionado, ano: anoSelecionado })),            // 5
        withTimeout(api.get(`/monitorizacao/seccoes/${anoSelecionado}`)),                            // 6
        withTimeout(api.get(`/monitorizacao/jogos/${anoSelecionado}`)),                              // 7
      ]);

      const getValue = (result, defaultValue = null) => {
        return result.status === 'fulfilled' ? result.value : { data: defaultValue };
      };

      const [rankingMensalRes, objetivosAnuaisRes, objetivosMensaisRes,
             vendasMensaisRes, vendasAnuaisRes, statsRes, monitorizacaoAnualRes, monitorizacaoJogosRes] = [
        getValue(fastResults[0], []),
        getValue(fastResults[1], null),
        getValue(fastResults[2], null),
        getValue(fastResults[3], []),
        getValue(fastResults[4], []),
        getValue(fastResults[5], {}),
        getValue(fastResults[6], {}),
        getValue(fastResults[7], {}),
      ];

      const monitorizacaoMensalRes = getValue(fastResults[5], { vendas_por_seccao: [] }); // statsRes reused

      fastResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`❌ Erro na API rápida ${index}:`, result.reason?.message || result.reason);
        }
      });
      
      setRankingMensal(rankingMensalRes.data || []);
      // rankingAnual movido para Fase 2 (cold start lento)
      
      // Processar objetivos do novo sistema
      const objetivosTransformados = [];
      const gruposTransformados = [];
      
      // Processar objetivos anuais
      if (objetivosAnuaisRes?.data?.secoes && typeof objetivosAnuaisRes.data.secoes === 'object') {
        // ORDEM FIXA para apresentação (sempre a mesma)
        const ordemSeccoes = ['cafe', 'raspadinhas', 'loja_avelal', 'loja_satao', 'restaurante', 'escritorio', 'jogos'];
        
        ordemSeccoes.forEach(secaoKey => {
          const dados = objetivosAnuaisRes.data.secoes[secaoKey];
          if (!dados) return;
          
          // Admin vê TODAS as secções, mesmo com 0
          if (dados.anual !== undefined) {
            objetivosTransformados.push({
              id: `anual-${secaoKey}`,
              _id: `anual-${secaoKey}`,
              tipo: 'anual',
              seccao: MAPEAMENTO_SECOES[secaoKey] || secaoKey,
              valor: dados.anual || 0,
              mes: null,
              ano: anoSelecionado
            });
          }
        });
        
        // Processar grupos anuais
        if (objetivosAnuaisRes.data.grupos && Array.isArray(objetivosAnuaisRes.data.grupos)) {
          objetivosAnuaisRes.data.grupos.forEach(grupo => {
            const grupoTransformado = {
              ...grupo,
              tipo: 'anual'
            };
            gruposTransformados.push(grupoTransformado);
          });
        }
      }
      
      // Processar objetivos mensais
      if (objetivosMensaisRes?.data?.secoes && typeof objetivosMensaisRes.data.secoes === 'object') {
        // ORDEM FIXA para apresentação (sempre a mesma)
        const ordemSeccoes = ['cafe', 'raspadinhas', 'loja_avelal', 'loja_satao', 'restaurante', 'escritorio', 'jogos'];
        
        ordemSeccoes.forEach(secaoKey => {
          const dados = objetivosMensaisRes.data.secoes[secaoKey];
          if (!dados) return;
          
          // Admin vê TODAS as secções, mesmo com 0
          if (dados.mensal !== undefined) {
            objetivosTransformados.push({
              id: `mensal-${mesSelecionado}-${secaoKey}`,
              _id: `mensal-${mesSelecionado}-${secaoKey}`,
              tipo: 'mensal',
              seccao: MAPEAMENTO_SECOES[secaoKey] || secaoKey,
              valor: dados.mensal || 0,
              mes: mesSelecionado,
              ano: anoSelecionado
            });
          }
        });
        
        // Processar grupos mensais
        if (objetivosMensaisRes.data.grupos && Array.isArray(objetivosMensaisRes.data.grupos)) {
          objetivosMensaisRes.data.grupos.forEach(grupo => {
            gruposTransformados.push({
              ...grupo,
              tipo: 'mensal'
            });
          });
        }
      }
      
      setObjetivos(objetivosTransformados);
      setObjetivosAgrupados(gruposTransformados);
      
      setVendas(vendasMensaisRes.data || []);
      setVendasAnuais(vendasAnuaisRes.data || []);
      setStats(statsRes.data || {});
      
      // Calcular total anual de Jogos e adicionar ao monitorizacaoRes
      let totalJogosAnual = 0;
      if (monitorizacaoJogosRes && monitorizacaoJogosRes.data && typeof monitorizacaoJogosRes.data === 'object') {
        Object.values(monitorizacaoJogosRes.data).forEach(jogo => {
          totalJogosAnual += jogo.total_anual || 0;
        });
      }
      
      // Adicionar "Jogos" ao objeto de monitorização
      const monitorizacaoComJogos = { ...monitorizacaoAnualRes };
      if (monitorizacaoComJogos.data) {
        monitorizacaoComJogos.data['Jogos'] = {
          total_vendas_anual: totalJogosAnual
        };
      }
      
      // 🛡️ Só atualizar estado se monitorizacaoAnualRes trouxe dados reais — se foi
      // timeout/erro, preservar o estado anterior em vez de zerar UI.
      if (monitorizacaoAnualRes?.data && Object.keys(monitorizacaoAnualRes.data).length > 0) {
        setMonitorizacaoRes(monitorizacaoComJogos);
      }
      
      // Calcular vendas mensais da monitorização
      const vendasMensaisMonitorizacao = {};
      if (monitorizacaoMensalRes && monitorizacaoMensalRes.data && monitorizacaoMensalRes.data.vendas_por_seccao) {
        monitorizacaoMensalRes.data.vendas_por_seccao.forEach(item => {
          vendasMensaisMonitorizacao[item.seccao] = {
            total: item.total,
            quantidade: item.quantidade || 0
          };
        });
      }
      
      setVendasPorSeccaoMonitorizacao(vendasMensaisMonitorizacao);

      // Adicionar Jogos aos objetivos mensais se houver vendas (mesmo sem objetivo definido)
      if (vendasMensaisMonitorizacao['Jogos'] && vendasMensaisMonitorizacao['Jogos'].total > 0) {
        // Verificar se já existe objetivo para Jogos
        const temObjetivoJogos = objetivosTransformados.some(obj => obj.tipo === 'mensal' && obj.seccao === 'Jogos');
        
        if (!temObjetivoJogos) {
          // Adicionar Jogos com objetivo 0 para mostrar apenas o progresso
          objetivosTransformados.push({
            id: `mensal-${mesSelecionado}-jogos`,
            _id: `mensal-${mesSelecionado}-jogos`,
            tipo: 'mensal',
            seccao: 'Jogos',
            valor: 0, // Sem objetivo definido
            mes: mesSelecionado,
            ano: anoSelecionado
          });
        }
      }

      // 🚀 Fase 1 concluída — esconder spinner; restantes XD calls são em background
      setLoading(false);
      setLoadingXd(true);

      // 🚀 Fase 2 (background): XD calls que podem ser lentas (cold-cache + Tailscale)
      // Não bloqueiam o render do dashboard. Cada uma actualiza o seu próprio estado.
      Promise.allSettled([
        dashboardAPI.getRankingAnual({ ano: anoSelecionado }),                  // 0
        dashboardAPI.getContribuicoesXdMes(anoSelecionado, mesSelecionado),     // 1
        dashboardAPI.getContribuicoesXdAno(anoSelecionado),                     // 2
        dashboardAPI.getPontosXdMes(anoSelecionado, mesSelecionado),            // 3
        dashboardAPI.getPontosXdAno(anoSelecionado),                            // 4
        api.get('/xd-mapping/colaboradores-pendentes-com-vendas'),               // 5
      ]).then((xdResults) => {
        xdResults.forEach((r, i) => {
          if (r.status === 'rejected') {
            console.warn(`⏳ XD background API ${i} falhou:`, r.reason?.message || r.reason);
          }
        });
        if (xdResults[0].status === 'fulfilled' && xdResults[0].value?.data) {
          setRankingAnual(xdResults[0].value.data);
        }
        if (xdResults[1].status === 'fulfilled' && xdResults[1].value?.data) {
          setContribuicoesXdMes(xdResults[1].value.data);
        }
        if (xdResults[2].status === 'fulfilled' && xdResults[2].value?.data) {
          setContribuicoesXdAno(xdResults[2].value.data);
        }
        if (xdResults[3].status === 'fulfilled' && xdResults[3].value?.data) {
          setPontosXdMes(xdResults[3].value.data);
        }
        if (xdResults[4].status === 'fulfilled' && xdResults[4].value?.data) {
          setPontosXdAno(xdResults[4].value.data);
        }
        if (xdResults[5].status === 'fulfilled' && xdResults[5].value?.data) {
          setPendentesMapping(xdResults[5].value.data);
        }
      }).finally(() => {
        // 🛡️ finally garante que loadingXd vai a false mesmo se uma das setters explodir
        setLoadingXd(false);
      });
    } catch (error) {
      console.error('Erro ao carregar dados', error);
      setLoading(false);
    }
  };

  // Filtrar objetivos
  const objetivosMensais = useMemo(() => 
    (objetivos && Array.isArray(objetivos)) ? objetivos.filter(o => o.tipo === 'mensal' && o.mes === mesSelecionado) : [],
    [objetivos, mesSelecionado]
  );
  
  // Vendas Globais (Negócio): TODAS as secções — XD Source of Truth
  const vendasGlobaisMes = useMemo(() => {
    const resultado = {};
    const monData = monitorizacaoRes?.data;
    if (!monData || typeof monData !== 'object') return resultado;

    const mesKey = MESES_KEYS[mesSelecionado - 1];

    // Ordem fixa — Loja Satao consolidada
    const SECCOES_GLOBAIS = ['Café', 'Loja Avelal', 'Loja Sátão', 'Escritório'];

    SECCOES_GLOBAIS.forEach(sec => {
      const dados = monData[sec];
      if (!dados) return;

      let valor = dados.vendas_mensais?.[mesKey] || 0;
      if (dados.hibrida && dados.vendas_b2b_mensais) {
        valor += dados.vendas_b2b_mensais[mesKey] || 0;
      }
      if (valor > 0) {
        resultado[sec] = { total: valor };
      }
    });

    // Secções internas (Raspadinhas, Jogos, etc.)
    ['Raspadinhas', 'Restaurante', 'Jogos'].forEach(sec => {
      const dados = monData[sec];
      if (!dados) return;
      const valor = sec === 'Jogos'
        ? (dados.total_vendas_anual > 0 ? (dados.vendas_mensais?.[mesKey] || 0) : 0)
        : (dados.vendas_mensais?.[mesKey] || 0);
      if (valor > 0) {
        resultado[sec] = { total: valor };
      }
    });

    return resultado;
  }, [monitorizacaoRes, mesSelecionado]);

  const totalVendasGlobaisMes = useMemo(() =>
    Object.values(vendasGlobaisMes).reduce((sum, s) => sum + (s.total || 0), 0),
    [vendasGlobaisMes]
  );

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="dashboard-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <p className="text-sm text-muted-foreground">A carregar dados...</p>
    </div>
  );

  // Calcular totais para KPI — GLOBAL (todas as seccoes)
  const totalVendasMes = totalVendasGlobaisMes;
  const totalObjetivoMes = objetivosMensais.reduce((sum, obj) => sum + (obj.valor || 0), 0);
  const pctAtingimento = totalObjetivoMes > 0 ? Math.round((totalVendasMes / totalObjetivoMes) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {/* Cabeçalho + ModuleInfo */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ModuleInfo
            descricao="Painel com separacao clara: Negocio (vendas globais de todas as seccoes) vs Performance (contribuicao por colaborador, ranking e pontos via XD Source of Truth)."
            dados="KPIs e Vendas por Seccao = XD global (Cafe + Avelal + Satao + Escritorio). Contribuicao e Ranking = XD direto (via CreationUserId)."
            calculo="Progresso = vendas actuais (monitorizacao) / meta definida x 100. Pontos = vendas XD do turno vs thresholds fixos em regras_pontos."
            legendas="Todas as vendas provêm do XD Software (Source of Truth). Raspadinhas = pontos independentes."
          />
        </div>
      </div>
      <Card className="bg-gradient-card border-primary/20" data-testid="dashboard-filtros-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Calendar className="h-5 w-5 text-primary" />
            <span className="font-semibold">Período:</span>
            <NativeSelect
              value={mesSelecionado}
              onChange={(e) => setMesSelecionado(parseInt(e.target.value))}
              className="w-40"
              data-testid="dashboard-filtro-mes"
            >
              {MESES.map((mes, idx) => (
                <option key={idx} value={idx + 1}>{mes}</option>
              ))}
            </NativeSelect>
            <NativeSelect
              value={anoSelecionado}
              onChange={(e) => setAnoSelecionado(parseInt(e.target.value))}
              className="w-32"
              data-testid="dashboard-filtro-ano"
            >
              {Array.from({ length: new Date().getFullYear() - 2024 + 2 }, (_, i) => 2024 + i).map(ano => (
                <option key={ano} value={ano}>{ano}</option>
              ))}
            </NativeSelect>
          </div>
        </CardContent>
      </Card>

      {/* KPIs Resumo — Negócio Global (todas as secções) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-kpi-resumo">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">Vendas Globais Mes <DataOriginBadge type="xd" /></p>
            <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="kpi-total-vendas">
              {'\u20AC'}{totalVendasMes.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">Objectivo Mes</p>
            <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="kpi-objectivo">
              {totalObjetivoMes > 0 ? `\u20AC${totalObjetivoMes.toFixed(2)}` : 'N/D'}
            </p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${pctAtingimento >= 100 ? 'border-l-emerald-500' : pctAtingimento >= 75 ? 'border-l-amber-500' : 'border-l-red-500'}`}>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">Atingimento</p>
            <p className={`text-2xl font-bold mt-1 ${pctAtingimento >= 100 ? 'text-emerald-600' : pctAtingimento >= 75 ? 'text-amber-600' : 'text-red-600'}`} data-testid="kpi-atingimento">
              {totalObjetivoMes > 0 ? `${pctAtingimento}%` : 'N/D'}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Seccoes c/ Vendas</p>
            <p className="text-2xl font-bold text-violet-600 mt-1" data-testid="kpi-seccoes">
              {Object.keys(vendasGlobaisMes).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Objetivos Mensais e Anuais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dashboard-objetivos-grid">
        {/* Objetivos Mensais */}
        <Card data-testid="dashboard-objetivos-mensais">
          <CardHeader>
            <CardTitle>Objetivos Mensais - {MESES[mesSelecionado - 1]} {anoSelecionado}</CardTitle>
          </CardHeader>
          <CardContent>
            {objetivosMensais.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum objetivo mensal definido
              </div>
            ) : (
              <div className="space-y-4">
                {objetivosMensais.filter(obj => obj.valor > 0).map((obj) => {
                  const vendasSeccao = vendasPorSeccaoMonitorizacao[obj.seccao];
                  const atual = vendasSeccao?.total || 0;
                  const progresso = obj.valor > 0 ? (atual / obj.valor) * 100 : 0;
                  const breakdownSeccao = getBreakdownSeccao(obj.seccao, false);
                  const colabOrdenados = breakdownSeccao?.colaboradores
                    ? [...breakdownSeccao.colaboradores].sort((a, b) => b.total - a.total)
                    : [];
                  
                  return (
                    <div key={obj.id} className="p-4 rounded-lg bg-gradient-card border border-border" data-testid={`obj-mensal-${obj.seccao}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Target className="h-5 w-5 text-primary" />
                          <span className="font-semibold text-lg">{obj.seccao}</span>
                        </div>
                        <Badge variant={progresso >= 100 ? 'default' : 'secondary'} className="text-lg">
                          {progresso >= 100 ? 'Atingido!' : `${progresso.toFixed(1)}%`}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>Meta: {'\u20AC'}{obj.valor.toLocaleString('pt-PT')}</span>
                        <span>Atual: {'\u20AC'}{atual.toFixed(2)}</span>
                      </div>
                      <Progress value={Math.min(progresso, 100)} className="h-3" />
                      
                      {/* Contribuicao por colaborador — expansivel (open by default) */}
                      {(colabOrdenados.length > 0 || loadingXd) && (() => {
                        const teamKey = `mensal-${obj.seccao}`;
                        const expanded = isTeamExpanded(teamKey);
                        const badge = seccaoBadge(obj.seccao, breakdownSeccao?.fonte);
                        return (
                          <div className="mt-4 pt-3 border-t border-border">
                            <button
                              onClick={() => toggleTeam(teamKey)}
                              className="flex items-center gap-2 mb-2 w-full text-left cursor-pointer hover:opacity-80 transition-opacity"
                              data-testid={`toggle-team-mensal-${obj.seccao}`}
                            >
                              <Users className="h-3.5 w-3.5 text-primary" />
                              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Equipa</span>
                              {badge && (
                                <Badge variant="outline" className={`text-[10px] ${badge.classes}`}>
                                  {badge.label}
                                </Badge>
                              )}
                              <span className="ml-auto">
                                {expanded
                                  ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                              </span>
                            </button>
                            {expanded && (
                              loadingXd && colabOrdenados.length === 0 ? (
                                <div className="flex items-center gap-2 py-2 px-3 text-xs text-muted-foreground">
                                  <span className="inline-block w-3 h-3 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></span>
                                  A carregar equipa...
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  {colabOrdenados.map((colab, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-1.5 px-3 rounded bg-muted/30" data-testid={`obj-mensal-colab-${idx}`}>
                                      <span className="text-sm font-medium">{colab.colaborador_nome}</span>
                                      <div className="flex items-center gap-3">
                                        <span className="text-sm font-semibold text-success">{'\u20AC'}{colab.total.toFixed(2)}</span>
                                        <Badge variant="outline" className="text-xs">{colab.percentagem.toFixed(1)}%</Badge>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Objetivos Anuais */}
        <Card data-testid="dashboard-objetivos-anuais">
          <CardHeader>
            <CardTitle>Objetivos Anuais - {anoSelecionado}</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const objetivosAnuais = objetivos.filter(o => o.tipo === 'anual');
              
              if (objetivosAnuais.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum objetivo anual definido
                  </div>
                );
              }

              // Usar vendas da monitorização (inclui vendas manuais + fechos + vendas diretas)
              const vendasAnuaisPorSeccao = {};
              if (monitorizacaoRes && monitorizacaoRes.data && typeof monitorizacaoRes.data === 'object') {
                Object.entries(monitorizacaoRes.data).forEach(([seccao, dados]) => {
                  vendasAnuaisPorSeccao[seccao] = dados.total_vendas_anual || 0;
                });
              }

              return (
                <div className="space-y-4">
                  {objetivosAnuais.filter(obj => obj.valor > 0).map((obj) => {
                    const atual = vendasAnuaisPorSeccao[obj.seccao] || 0;
                    const progresso = obj.valor > 0 ? (atual / obj.valor) * 100 : 0;
                    const breakdownSeccao = getBreakdownSeccao(obj.seccao, true);
                    const colabOrdenados = breakdownSeccao?.colaboradores
                      ? [...breakdownSeccao.colaboradores].sort((a, b) => b.total - a.total)
                      : [];
                    
                    return (
                      <div key={obj.id} className="p-4 rounded-lg bg-gradient-card border border-border" data-testid={`obj-anual-${obj.seccao}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Target className="h-5 w-5 text-accent" />
                            <span className="font-semibold text-lg">{obj.seccao}</span>
                          </div>
                          <Badge variant={progresso >= 100 ? 'default' : 'secondary'} className="text-lg">
                            {progresso >= 100 ? 'Atingido!' : `${progresso.toFixed(1)}%`}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                          <span>Meta: {'\u20AC'}{obj.valor.toLocaleString('pt-PT')}</span>
                          <span>Atual: {'\u20AC'}{atual.toFixed(2)}</span>
                        </div>
                        <Progress value={Math.min(progresso, 100)} className="h-3" />
                        
                        {/* Contribuicao por colaborador — expansivel (open by default) */}
                        {(colabOrdenados.length > 0 || loadingXd) && (() => {
                          const teamKey = `anual-${obj.seccao}`;
                          const expanded = isTeamExpanded(teamKey);
                          const badge = seccaoBadge(obj.seccao, breakdownSeccao?.fonte);
                          return (
                            <div className="mt-4 pt-3 border-t border-border">
                              <button
                                onClick={() => toggleTeam(teamKey)}
                                className="flex items-center gap-2 mb-2 w-full text-left cursor-pointer hover:opacity-80 transition-opacity"
                                data-testid={`toggle-team-anual-${obj.seccao}`}
                              >
                                <Users className="h-3.5 w-3.5 text-accent" />
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Equipa</span>
                                {badge && (
                                  <Badge variant="outline" className={`text-[10px] ${badge.classes}`}>
                                    {badge.label}
                                  </Badge>
                                )}
                                <span className="ml-auto">
                                  {expanded
                                    ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                    : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                                </span>
                              </button>
                              {expanded && (
                                loadingXd && colabOrdenados.length === 0 ? (
                                  <div className="flex items-center gap-2 py-2 px-3 text-xs text-muted-foreground">
                                    <span className="inline-block w-3 h-3 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></span>
                                    A carregar equipa...
                                  </div>
                                ) : (
                                  <div className="space-y-1.5">
                                    {colabOrdenados.map((colab, idx) => (
                                      <div key={idx} className="flex items-center justify-between py-1.5 px-3 rounded bg-muted/30" data-testid={`obj-anual-colab-${idx}`}>
                                        <span className="text-sm font-medium">{colab.colaborador_nome}</span>
                                        <div className="flex items-center gap-3">
                                          <span className="text-sm font-semibold text-success">{'\u20AC'}{colab.total.toFixed(2)}</span>
                                          <Badge variant="outline" className="text-xs">{colab.percentagem.toFixed(1)}%</Badge>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Objetivos Agrupados */}
      {objetivosAgrupados.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-500" />
              Objetivos Agrupados - {anoSelecionado}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {objetivosAgrupados.filter(grupo => grupo.mostrarAdmin !== false).map((grupo) => {
                const mesesNomesCompletos = {
                  'janeiro': 'Jan', 'fevereiro': 'Fev', 'marco': 'Mar', 'abril': 'Abr',
                  'maio': 'Mai', 'junho': 'Jun', 'julho': 'Jul', 'agosto': 'Ago',
                  'setembro': 'Set', 'outubro': 'Out', 'novembro': 'Nov', 'dezembro': 'Dez'
                };
                
                const mesesNomes = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho',
                                   'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
                
                // Grupos de meses específicos (TRIMESTRAIS)
                if (grupo.tipo === 'meses_especificos' && grupo.mesesSelecionados && grupo.mesesAgrupados) {
                  let metaTotal = 0;
                  let vendasEquipa = 0;
                  let minhasVendas = 0;
                  
                  // Calcular objetivos para cada mês selecionado e cada seção
                  if (Array.isArray(grupo.mesesSelecionados)) {
                    grupo.mesesSelecionados.forEach(mesNome => {
                      const mesIdx = mesesNomes.indexOf(mesNome);
                      
                      if (Array.isArray(grupo.secoes)) {
                        grupo.secoes.forEach(seccaoNome => {
                          // Encontrar o objetivo para esta seção e mês
                          const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a')] || seccaoNome;
                          const objetivo = (objetivos && Array.isArray(objetivos)) ? objetivos.find(o => 
                            o.tipo === 'mensal' && 
                            o.seccao === seccaoNormalizada && 
                            o.mes === mesIdx + 1
                          ) : null;
                          
                          if (objetivo) {
                            metaTotal += objetivo.valor;
                          }
                          
                          // Calcular vendas da equipa para este mês e seção
                          const vendasMesSeccao = (vendasAnuais && Array.isArray(vendasAnuais)) ? vendasAnuais.filter(v => 
                            v.seccao === seccaoNormalizada &&
                            new Date(v.data).getMonth() === mesIdx
                          ) : [];
                          vendasEquipa += vendasMesSeccao.reduce((sum, v) => sum + v.valor, 0);
                          
                          // Calcular minhas vendas para este mês e seção
                          const minhasVendasMesSeccao = vendasMesSeccao.filter(v => String(v.colaborador_id) === String(user?.id));
                          minhasVendas += minhasVendasMesSeccao.reduce((sum, v) => sum + v.valor, 0);
                        });
                      }
                    });
                  }
                  
                  const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                  const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                  const mesesStr = Array.isArray(grupo.mesesSelecionados) ? grupo.mesesSelecionados.map(m => mesesNomesCompletos[m] || m).join('-') : '';
                  
                  return (
                    <div key={grupo.id} className="p-4 rounded-lg bg-gradient-card border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Award className="h-5 w-5 text-cyan-500" />
                          <span className="font-semibold text-lg">{grupo.nome}</span>
                          <span className="text-xs px-2 py-1 rounded bg-cyan-100 text-cyan-700">
                            📊 {mesesStr}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                          </Badge>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Minha: {minhaContribuicao.toFixed(1)}%
                          </Badge>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        {Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : ''}
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                      </div>
                      <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                    </div>
                  );
                }
                
                // Grupos MENSAIS - mostrar apenas o mês atual
                else if (grupo.tipo === 'mensal') {
                  let metaTotal = 0;
                  let vendasEquipa = 0;
                  let minhasVendas = 0;
                  
                  
                  // Somar apenas o mês selecionado (mês atual)
                  if (Array.isArray(grupo.secoes)) {
                    grupo.secoes.forEach(seccaoNome => {
                      // Normalizar nome da seção
                      const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                      const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                      
                      
                      const objetivo = (objetivos && Array.isArray(objetivos)) ? objetivos.find(o => 
                        o.tipo === 'mensal' && 
                        o.seccao === seccaoNormalizada && 
                        o.mes === mesSelecionado
                      ) : null;
                      
                      
                      if (objetivo) {
                        metaTotal += objetivo.valor;
                      }
                      
                      // Vendas do mês — fonte oficial: vendas_por_seccao do /api/dashboard/stats
                      // vendas_mensais_manual CONGELADO (dados legados, não alimentam totais)
                      const vendaSeccaoData = vendasPorSeccaoMonitorizacao[seccaoNome] || 
                                              vendasPorSeccaoMonitorizacao[seccaoNormalizada] || {};
                      const vendaSeccao = vendaSeccaoData.total || 0;
                      vendasEquipa += vendaSeccao;
                      
                      // Para minhas vendas, por enquanto não temos dados separados por colaborador
                      // Isso precisaria de um endpoint específico
                      minhasVendas += 0;
                    });
                  }
                  
                  
                  const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                  const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                  const mesNome = MESES[mesSelecionado - 1];
                  
                  return (
                    <div key={grupo.id} className="p-4 rounded-lg bg-gradient-card border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Target className="h-5 w-5 text-purple-500" />
                          <span className="font-semibold text-lg">{grupo.nome}</span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                            📅 {mesNome}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                          </Badge>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Minha: {minhaContribuicao.toFixed(1)}%
                          </Badge>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        {Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : ''}
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                      </div>
                      <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                    </div>
                  );
                }
                
                // Grupos ANUAIS - sempre exibir
                else if (grupo.tipo === 'anual') {
                  let metaTotal = 0;
                  let vendasEquipa = 0;
                  let minhasVendas = 0;
                  
                  
                  // Somar objetivos anuais das seções incluídas
                  if (Array.isArray(grupo.secoes)) {
                    grupo.secoes.forEach(seccaoNome => {
                      // Normalizar nome da seção
                      const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                      const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                      
                      
                      const objetivo = (objetivos && Array.isArray(objetivos)) ? objetivos.find(o => 
                        o.tipo === 'anual' && 
                        o.seccao === seccaoNormalizada
                      ) : null;
                      
                      
                      if (objetivo) {
                        metaTotal += objetivo.valor;
                      }
                      
                      // Calcular vendas anuais da equipa usando dados agregados
                      const totalSeccao = stats?.vendas_anuais_por_seccao?.[seccaoNormalizada] || 0;
                      vendasEquipa += totalSeccao;
                      
                      // Calcular minhas vendas anuais (usando dados agregados quando disponíveis)
                      const minhasVendasSeccao = stats?.minhas_vendas_anuais_por_seccao?.[seccaoNormalizada] || 0;
                      minhasVendas += minhasVendasSeccao;
                    });
                  }
                  
                  
                  const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                  const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                  
                  return (
                    <div key={grupo.id} className="p-4 rounded-lg bg-gradient-card border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Award className="h-5 w-5 text-green-500" />
                          <span className="font-semibold text-lg">{grupo.nome}</span>
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                            📆 Anual
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                          </Badge>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Minha: {minhaContribuicao.toFixed(1)}%
                          </Badge>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        {Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : ''}
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                      </div>
                      <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                    </div>
                  );
                }
                
                return null;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Objetivos Personalizados Ativos */}
      <ObjetivosPersonalizadosAtivos />

      {/* Pontos por Turno — Ranking Estilo Card (Source of Truth) */}
      <div data-testid="dashboard-pontos-xd">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold">Pontos por Turno</h3>
          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
            XD
          </Badge>
          {user?.role === 'admin' && (
          <button
            type="button"
            data-testid="btn-recalcular-pontos-xd"
            disabled={recalculandoXd}
            onClick={async () => {
              if (recalculandoXd) return;
              setRecalculandoXd(true);
              try {
                const token = localStorage.getItem('token');
                const apiUrl = process.env.REACT_APP_BACKEND_URL;
                // Invalidar cache + recalcular live com persistência
                await fetch(`${apiUrl}/api/pontos-xd/cache/invalidar/${anoSelecionado}/${mesSelecionado}`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const resp = await fetch(`${apiUrl}/api/pontos-xd/calcular?ano=${anoSelecionado}&mes=${mesSelecionado}&persistir=true`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await resp.json();
                if (!resp.ok) {
                  throw new Error(data?.detail || `HTTP ${resp.status}`);
                }
                // Recarregar dados do dashboard
                if (typeof loadData === 'function') await loadData();
                alert(`✅ Pontos XD recalculados\nDias com turnos: ${data.dias_com_turnos || 0}\nXD disponível: ${data.xd_disponivel ? 'sim' : 'não'}`);
              } catch (e) {
                alert('❌ Erro ao recalcular: ' + (e?.message || e));
              } finally {
                setRecalculandoXd(false);
              }
            }}
            className={`ml-auto inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${recalculandoXd ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-wait' : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
            title={`Recálculo manual (opcional). O Dashboard já recalcula automaticamente quando a cache fica obsoleta.`}
          >
            {recalculandoXd ? '⏳ A recalcular…' : '🔄 Recalcular XD'}
          </button>
          )}
        </div>

        {/* 🆕 06-06-2026 — Aviso colaboradores XD com vendas SEM mapping validado.
            🛡️ 12-02-2026 — Já NÃO são excluídos do ranking (correm com nome XD).
            Mas o admin ainda deve validar para associar a um user Mongo (ajustes, login). */}
        {pendentesMapping && pendentesMapping.total > 0 && (
          <div
            className="mb-4 p-3 rounded-lg border border-blue-500/40 bg-blue-500/10 flex items-start gap-3"
            data-testid="alerta-mapping-xd"
          >
            <span className="text-blue-500 text-xl leading-none">ℹ️</span>
            <div className="flex-1 text-sm">
              <div className="font-semibold mb-1">
                {pendentesMapping.total} colaborador{pendentesMapping.total > 1 ? 'es' : ''} XD por associar a um user Mongo
              </div>
              <div className="text-muted-foreground mb-2">
                Já aparecem no ranking automaticamente com o nome XD. Validar o mapping permite associar a um user Mongo (necessário para ajustes manuais, raspadinhas e login):
              </div>
              <ul className="space-y-1">
                {pendentesMapping.pendentes.slice(0, 5).map((p) => (
                  <li key={p.xd_user_id} data-testid={`pendente-${p.xd_user_id}`}>
                    <strong>{p.nome_xd}</strong> — €{p.vendas_mes.toFixed(0)} este mês ({p.docs_mes} docs)
                    {p.seccoes.length > 0 && (
                      <span className="text-muted-foreground"> · {p.seccoes.join(', ')}</span>
                    )}
                  </li>
                ))}
                {pendentesMapping.pendentes.length > 5 && (
                  <li className="text-muted-foreground">
                    ...e mais {pendentesMapping.pendentes.length - 5}
                  </li>
                )}
              </ul>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  // Navegar para Sistema → Mapping XD
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('admin-navigate', { detail: { tab: 'sistema', subTab: 'mapping' } }));
                  }
                }}
                className="mt-2 inline-block text-blue-700 dark:text-blue-400 hover:underline text-xs font-medium"
                data-testid="link-validar-mappings"
              >
                Ir para Sistema → Mapping XD para associar →
              </a>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ranking Mensal */}
          <Card data-testid="pontos-xd-mensal">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold" data-testid="pontos-mensal-title">
                Ranking Mensal - {MESES[mesSelecionado - 1]}
              </CardTitle>
              {pontosXdMes && (
                <p className="text-xs text-muted-foreground">
                  {pontosXdMes.dias_com_turnos || 0} dias com turnos | {pontosXdMes.num_mappings_validados || 0} colab. validados
                  {pontosXdMes.xd_disponivel === false && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] font-medium">
                      ⚠ XD temporariamente indisponível — recálculo automático na próxima abertura
                    </span>
                  )}
                  {pontosXdMes.data_calculo && (
                    <span className="ml-2 text-[10px] text-gray-400" title="Cache automaticamente refrescada quando obsoleta (mês actual: 30min)">
                      · {(() => {
                        try {
                          const d = new Date(pontosXdMes.data_calculo);
                          const diff = Math.floor((Date.now() - d.getTime()) / 60000);
                          if (diff < 1) return 'agora';
                          if (diff < 60) return `há ${diff}min`;
                          if (diff < 1440) return `há ${Math.floor(diff/60)}h`;
                          return `há ${Math.floor(diff/1440)}d`;
                        } catch { return ''; }
                      })()}
                    </span>
                  )}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {loadingXd && !pontosXdMes ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-sm gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></span>
                  A carregar pontos XD...
                </div>
              ) : !pontosXdMes || pontosXdMes.colaboradores?.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  {pontosXdMes?.erro
                    ? `Erro: ${pontosXdMes.erro}`
                    : pontosXdMes?.dias_com_turnos === 0
                      ? 'Sem horarios configurados para este mes.'
                      : 'Sem dados de pontos'}
                </div>
              ) : (
                <div className="space-y-2">
                  {pontosXdMes.colaboradores.map((colab) => {
                    const isTop3 = colab.posicao <= 3;
                    return (
                      <div
                        key={colab.colaborador_id}
                        data-testid={`pontos-mensal-item-${colab.posicao}`}
                        className={`relative rounded-xl border px-4 py-3 transition-all ${
                          isTop3 ? 'bg-[#f0fafa] border-[#d0eded]' : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 mt-0.5 ${
                            isTop3 ? 'bg-amber-400 text-white' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {colab.posicao}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900 uppercase tracking-wide text-sm">
                              {colab.nome}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {'\u20AC'}{(colab.total_vendas || 0).toFixed(2)} em vendas
                              {colab.total_turnos > 0 && <span className="ml-2">({colab.total_turnos} turnos)</span>}
                            </p>
                            <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                              {Object.entries(colab.por_seccao || {}).map(([sec, dados]) => (
                                <span key={sec}>
                                  <span className="font-medium text-gray-500">{sec}:</span>{' '}
                                  {'\u20AC'}{(dados.vendas || 0).toFixed(2)}
                                </span>
                              ))}
                            </div>
                          </div>
                          <span
                            data-testid={`pontos-mensal-pts-${colab.posicao}`}
                            className="shrink-0 inline-flex items-center rounded-full px-3 py-1 text-sm font-bold text-white"
                            style={{ backgroundColor: '#38bec9' }}
                          >
                            {colab.total_pontos} pts
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ranking Anual */}
          <Card data-testid="pontos-xd-anual">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold" data-testid="pontos-anual-title">
                Ranking Anual - {anoSelecionado}
              </CardTitle>
              {pontosXdAno?.meses_calculados > 0 && (
                <p className="text-xs text-muted-foreground">
                  {pontosXdAno.meses_calculados} meses calculados
                </p>
              )}
            </CardHeader>
            <CardContent>
              {loadingXd && !pontosXdAno ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-sm gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></span>
                  A carregar pontos anuais...
                </div>
              ) : !pontosXdAno || pontosXdAno.colaboradores?.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  {pontosXdAno?.erro ? `Erro: ${pontosXdAno.erro}` : 'Sem dados de pontos anuais'}
                </div>
              ) : (
                <div className="space-y-2">
                  {pontosXdAno.colaboradores.map((colab) => {
                    const isTop3 = colab.posicao <= 3;
                    return (
                      <div
                        key={colab.colaborador_id}
                        data-testid={`pontos-anual-item-${colab.posicao}`}
                        className={`relative rounded-xl border px-4 py-3 transition-all ${
                          isTop3 ? 'bg-[#f0fafa] border-[#d0eded]' : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 mt-0.5 ${
                            isTop3 ? 'bg-amber-400 text-white' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {colab.posicao}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900 uppercase tracking-wide text-sm">
                              {colab.nome}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {'\u20AC'}{(colab.total_vendas || 0).toFixed(2)} em vendas
                              {colab.total_turnos > 0 && <span className="ml-2">({colab.total_turnos} turnos)</span>}
                            </p>
                            {colab.por_mes && (
                              <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                {Object.entries(colab.por_mes).map(([mes, dados]) => (
                                  <span key={mes}>
                                    <span className="font-medium text-gray-500">{MESES[parseInt(mes) - 1]?.substring(0, 3)}:</span>{' '}
                                    {'\u20AC'}{(dados.vendas || 0).toFixed(2)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <span
                            data-testid={`pontos-anual-pts-${colab.posicao}`}
                            className="shrink-0 inline-flex items-center rounded-full px-3 py-1 text-sm font-bold text-white"
                            style={{ backgroundColor: '#38bec9' }}
                          >
                            {colab.total_pontos} pts
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
};

// Componente para Objetivos Personalizados Ativos
const ObjetivosPersonalizadosAtivos = () => {
  const [periodosAtivos, setPeriodosAtivos] = useState([]);
  const [vendasPeriodo, setVendasPeriodo] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarObjetivosAtivos();
  }, []);

  const carregarObjetivosAtivos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Buscar períodos ativos
      const response = await fetch(`${API_URL}/api/objetivos-personalizados/ativos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const periodos = await response.json();
      
      if (periodos.length === 0) {
        setPeriodosAtivos([]);
        return;
      }
      
      // Para cada período, buscar as vendas atuais
      const vendasPorPeriodo = {};
      for (const periodo of periodos) {
        const hoje = new Date().toISOString().split('T')[0];
        const dataInicio = periodo.data_inicio;
        const dataFim = hoje < periodo.data_fim ? hoje : periodo.data_fim; // Até hoje ou até o fim do período
        
        const vendasResponse = await fetch(
          `${API_URL}/api/vendas/totais-por-secao-periodo?data_inicio=${dataInicio}&data_fim=${dataFim}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        vendasPorPeriodo[periodo.id] = await vendasResponse.json();
      }
      
      setPeriodosAtivos(periodos);
      setVendasPeriodo(vendasPorPeriodo);
    } catch (error) {
      console.error('Erro ao carregar objetivos personalizados ativos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || periodosAtivos.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-purple-500" />
          Objetivos Personalizados Ativos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {periodosAtivos.map(periodo => {
            const vendas = vendasPeriodo[periodo.id] || {};
            const secoes = periodo.secoes || {};
            
            return (
              <div key={periodo.id} className="p-4 rounded-lg bg-gradient-card border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{periodo.nome}</h3>
                    <p className="text-sm text-muted-foreground">
                      {new Date(periodo.data_inicio).toLocaleDateString('pt-PT')} até{' '}
                      {new Date(periodo.data_fim).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(secoes).map(([secaoId, dados]) => {
                    const objetivo = dados.objetivo || 0;
                    if (objetivo === 0) return null;
                    
                    const vendaAtual = vendas[secaoId] || 0;
                    const progresso = objetivo > 0 ? (vendaAtual / objetivo) * 100 : 0;
                    const secaoNome = MAPEAMENTO_SECOES[secaoId] || secaoId;
                    
                    return (
                      <div key={secaoId} className="p-3 rounded-lg bg-white border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">{secaoNome}</span>
                          <Badge variant={progresso >= 100 ? 'default' : 'secondary'}>
                            {progresso.toFixed(1)}%
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                          <span>Meta: €{objetivo.toLocaleString('pt-PT')}</span>
                          <span>Atual: €{vendaAtual.toFixed(2)}</span>
                        </div>
                        <Progress value={Math.min(progresso, 100)} className="h-2" />
                      </div>
                    );
                  })}
                </div>
                
                {/* Grupos Agrupados */}
                {periodo.grupos && periodo.grupos.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold mb-3 text-gray-700">📦 Objetivos Agrupados</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {periodo.grupos.map(grupo => {
                        const objetivoTotal = grupo.secoes.reduce((total, secaoId) => {
                          return total + (secoes[secaoId]?.objetivo || 0);
                        }, 0);
                        
                        const vendaTotal = grupo.secoes.reduce((total, secaoId) => {
                          return total + (vendas[secaoId] || 0);
                        }, 0);
                        
                        const progressoGrupo = objetivoTotal > 0 ? (vendaTotal / objetivoTotal) * 100 : 0;
                        
                        return (
                          <div key={grupo.id} className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-bold text-blue-900">{grupo.nome}</span>
                              <Badge variant={progressoGrupo >= 100 ? 'default' : 'secondary'} className="text-base px-3 py-1">
                                {progressoGrupo.toFixed(1)}%
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-600 mb-2">
                              {grupo.secoes.map(id => MAPEAMENTO_SECOES[id] || id).join(' + ')}
                            </div>
                            <div className="flex items-center justify-between text-sm font-semibold mb-2">
                              <span className="text-green-700">Meta: €{objetivoTotal.toLocaleString('pt-PT')}</span>
                              <span className="text-blue-700">Atual: €{vendaTotal.toFixed(2)}</span>
                            </div>
                            <Progress value={Math.min(progressoGrupo, 100)} className="h-3" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDashboardTab;
;
